import { ErrorCodes } from "@mandoob/shared";

import type { TranslateFn } from "@/contexts/language";
import { getErrorCode, getErrorMessage } from "@/lib/utils";

/**
 * Localization for the API error envelope.
 *
 * The API sends `{ error: { code, message } }` where `message` is English and
 * written for operators — it can even be a transport-level string like
 * "getaddrinfo ENOTFOUND …". The `code` is the contract; this map turns it into
 * a string the driver can read. Anything unmapped falls back to a generic
 * translated message rather than showing the raw server text.
 */
const MESSAGE_KEYS: Record<string, string> = {
  [ErrorCodes.AuthInvalidSession]: "Mandoob:Error.SessionExpired",
  [ErrorCodes.AuthUnauthorized]: "Mandoob:Error.SessionExpired",
  [ErrorCodes.CommonNotFound]: "Mandoob:Error.NotFound",
  [ErrorCodes.CommonValidation]: "Mandoob:Error.Validation",
  [ErrorCodes.WabaWindowClosed]: "Mandoob:Thread.WindowClosedToast",
  [ErrorCodes.WabaTemplateNotApproved]: "Mandoob:Error.TemplateNotApproved",
  [ErrorCodes.WabaTemplateNotAllowed]: "Mandoob:Error.TemplateNotAllowed",
  [ErrorCodes.WabaInvalidPhoneNumber]: "Mandoob:Form.InvalidPhone",
  [ErrorCodes.WabaCannotMessageSelf]: "Mandoob:NewDelivery.CannotMessageSelf",
  [ErrorCodes.WabaClientBusyWithOtherDriver]: "Mandoob:NewDelivery.ClientBusy",
  [ErrorCodes.WabaClientHasActiveConversation]:
    "Mandoob:NewDelivery.ClientActive",
  [ErrorCodes.WabaSendFailed]: "Mandoob:Error.SendFailed",
  [ErrorCodes.DeliveryStatusLocked]: "Mandoob:Delivery.StatusLocked",
  [ErrorCodes.CreditsInsufficient]: "Mandoob:Credits.InsufficientToast",
  [ErrorCodes.CreditsExpired]: "Mandoob:Credits.ExpiredToast",
  [ErrorCodes.BillingPlanNotFound]: "Mandoob:Error.PlanNotFound",
  [ErrorCodes.BillingPaymentFailed]: "Mandoob:Error.PaymentFailed",
  [ErrorCodes.BillingGatewayUnavailable]: "Mandoob:Error.GatewayUnavailable",
  [ErrorCodes.LabelsNotConfigured]: "Mandoob:Label.NotConfigured",
  [ErrorCodes.LabelsExtractFailed]: "Mandoob:Label.ExtractFailed",
  // Emitted by the API's catch-all error middleware.
  InternalServerError: "Mandoob:Error.Unexpected",
};

/** Localized text for a bare error code (per-row import results carry these). */
export function messageForErrorCode(
  code: string | null | undefined,
  t: TranslateFn,
  fallbackKey = "AnErrorOccurred",
): string {
  const key = code ? MESSAGE_KEYS[code] : undefined;
  return t(key ?? fallbackKey);
}

/**
 * Localized text for a failed request. The English server message is kept out
 * of the UI but logged, so a code we have not mapped yet is still diagnosable
 * from the browser console.
 */
export function errorMessage(
  error: unknown,
  t: TranslateFn,
  fallbackKey = "AnErrorOccurred",
): string {
  const code = getErrorCode(error);
  if (!code || !MESSAGE_KEYS[code]) {
    console.warn("Unlocalized API error", code, getErrorMessage(error));
  }
  return messageForErrorCode(code, t, fallbackKey);
}
